import { Container, Heading, Loader, NoData, Select, TreeView } from "@/components"
import useTaskHierarchy from "./useTaskHierarchy";
import { tabs } from "./consts";
import { cn, taskLegend } from "@/lib/utils";
import { Button, Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui";
import { ArrowLeft, CalendarDays, ChevronRight, Clock, Folder } from "lucide-react";
import moment from "moment";

const TaskHierarchy = () => {
  const {
    breadcrumbData,
    isLoadingTaskList,
    taskList,
    selectedTask,
    onTaskChange,
    isTreeLoading,
    getTreeData,
    isTreeRefetching,
    selectedTab,
    onTabClick,
    onBackClick,
    isPredecessors
  } = useTaskHierarchy();
  
  return (
    <Container>
      {/* Header & Search */}
      <div className="flex flex-col lg:flex-row gap-2 lg:items-center lg:justify-between mb-6">
        <div className="flex flex-row items-center justify-between">
          <Heading title={breadcrumbData[breadcrumbData.length - 1]?.name || ''} breadcrumbData={breadcrumbData} />
        </div>
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="min-w-[200px] max-w-[250px]">
            <Select
              options={taskList?.map((data: any) => ({label: data?.taskName, value: data?.taskId?.toString(),}))}
              value={selectedTask?.taskId?.toString()}
              onChange={onTaskChange} 
              disabled={isLoadingTaskList}
              placeholder="Select Task"
            />
          </div>
          <Button className="bg-white hover:bg-white border-primary border-1" onClick={onBackClick}>
            <ArrowLeft /> Back to Gantt Chart
          </Button>
        </div>
      </div>

      <div>
        {selectedTask ?
          <div className="border-1 bg-white rounded-lg mt-5 overflow-hidden">
            <div className="bg-amber-50 p-3 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              {/* <div className="hidden lg:block h-[50px] w-[1px] bg-gray-300" /> */}
              <div className="flex flex-col md:flex-row items-center gap-1 text-[14px] font-medium">
                <Folder className="h-5"/>
                <span className="max-w-[200px]">{selectedTask?.categoryName}</span>
                <ChevronRight className="h-5"/>
                <Folder className="h-5"/>
                <span className="max-w-[200px]">{selectedTask?.subCategoryName}</span>
                <ChevronRight className="h-5"/>
                <span className="max-w-[200px]">{selectedTask?.taskName}</span>
              </div>
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex flex-row items-center gap-2 bg-blue-100 border-1 border-blue-200 text-blue-800 px-2 py-1 rounded-sm">
                  <Clock className="h-4 w-4" />
                  <div className="flex flex-col">
                    <span className="text-[12px] font-medium">Duration</span>
                    <span className="text-[10px] text-black">{selectedTask?.plannedDurationDays || "0"} Days</span>
                  </div>
                </div>
                <div className="flex flex-row items-center gap-2 bg-blue-100 border-1 border-blue-200 text-blue-800 px-2 py-1 rounded-sm">
                  <CalendarDays className="h-4 w-4" />
                  <div className="flex flex-col">
                    <span className="text-[12px] font-medium">Planned Start Date</span>
                    <span className="text-[10px] text-black">{selectedTask?.plannedStartDate ? moment(selectedTask?.plannedStartDate).format('MM-DD-YYYY') : ''}</span>
                  </div>
                </div>
                <div className="flex flex-row items-center gap-2 bg-blue-100 border-1 border-blue-200 text-blue-800 px-2 py-1 rounded-sm">
                  <CalendarDays className="h-4 w-4" />
                  <div className="flex flex-col">
                    <span className="text-[12px] font-medium">Planned End Date</span>
                    <span className="text-[10px] text-black">{selectedTask?.plannedEndDate ? moment(selectedTask?.plannedEndDate).format('MM-DD-YYYY') : ''}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-3 relative">
              <Tabs
                value={selectedTab}
                defaultValue="project-info"
                className="w-full my-3"
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
                  <TabsList className="bg-transparent gap-x-4 max-w-screen lg:max-w-auto overflow-scroll lg:overflow-none">
                    {[...tabs]?.map((data: any) => (
                      <TabsTrigger
                        key={data?.value}
                        value={data?.value}
                        onClick={() => onTabClick(data.value)}
                      >
                        {data?.title}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  <div className="flex flex-col md:flex-row md:gap-3">
                    {taskLegend.map((data: any, index: number) => <div key={`task-taskLegend-${index}`} className="flex flex-row gap-1 items-center">
                      <div className={cn("h-4 w-4 rounded-[2px]", data.className)} />
                      <span className="text-[12px]">{data?.label}</span>
                      </div>)}
                  </div>
                  <div className="text-[12px] text-blue-600 ">Click on nodes to see the {isPredecessors ? 'predecessors' : 'successor'} task</div>
                </div>
                <TabsContent value={selectedTab} className="mt-2">
                  {isTreeLoading || isTreeRefetching ? <Loader /> : <TreeView initialEdges={getTreeData.edges} initialNodes={getTreeData.nodes} isPredecessors={isPredecessors} />}
                </TabsContent>
              </Tabs>
            </div>
          </div> :
          <div className="flex items-center justify-center border-1 bg-white rounded-lg mt-5 p-4">
            <NoData />
          </div>
        }
      </div>
    </Container>
  )
}

export default TaskHierarchy;